import { useState } from 'react';
import { type Control, Controller, type FieldValues, type Path } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useBoolean } from 'ahooks';
import { Checkbox, FormControl, FormControlLabel, FormHelperText, Link } from '@mui/material';
import { CrossBoardAgreementDialog } from './cross-board-agreement-dialog';
import { PrivacyAgreementDialog } from './privacy-agreement-dialog';

interface IProps<T extends FieldValues> {
  control: Control<T>;
}

export const KoProtocol1 = <T extends FieldValues>({ control }: IProps<T>) => {
  const { t } = useTranslation();
  const [checked, setChecked] = useState([false, false, false, false]);
  const firstUncheckedIndex = checked.findIndex((c) => !c);
  const [open1, { setFalse: close1, setTrue: openDialog1 }] = useBoolean();
  const [open2, { setFalse: close2, setTrue: openDialog2 }] = useBoolean();

  return (
    <Controller
      name={'confirmed' as Path<T>}
      control={control}
      rules={{ required: '계속하려면 모든 필수 항목을 먼저 체크해 주세요.' }}
      render={({ field: { ref, ...field }, fieldState: { error } }) => {
        const onChange = (index: number, c: boolean) => {
          const newState = [...checked];
          newState[index] = c;
          setChecked(newState);
          // Update the form state to reflect the changes
          field.onChange(newState.every((item) => item));
        };

        const setRef = (index: number) => (firstUncheckedIndex === index ? ref : undefined);

        return (
          <>
            <FormControl required component="fieldset">
              <FormControlLabel
                label="[필수] 만 14세 이상입니다"
                control={
                  <Checkbox
                    checked={checked[0]}
                    slotProps={{ input: { ref: setRef(0) } }}
                    onChange={(e) => onChange(0, e.target.checked)}
                  />
                }
              />

              <FormControlLabel
                label={
                  <div>
                    [필수] 한국레노버 이용약관 동의
                    <Link
                      className="ml-4!"
                      target="_blank"
                      rel="noopener noreferrer"
                      href={t('legal_url', { lng: 'ko' })}
                    >
                      전문보기
                    </Link>
                  </div>
                }
                control={
                  <Checkbox
                    checked={checked[1]}
                    slotProps={{ input: { ref: setRef(1) } }}
                    onChange={(e) => onChange(1, e.target.checked)}
                  />
                }
              />

              <FormControlLabel
                label={
                  <div>
                    [필수] 개인정보 수집 및 이용 동의
                    <Link
                      role="button"
                      className="ml-4!"
                      onClick={(e) => {
                        e.preventDefault();
                        openDialog1();
                      }}
                    >
                      전문보기
                    </Link>
                  </div>
                }
                control={
                  <Checkbox
                    checked={checked[2]}
                    slotProps={{ input: { ref: setRef(2) } }}
                    onChange={(e) => onChange(2, e.target.checked)}
                  />
                }
              />

              <FormControlLabel
                label={
                  <div>
                    [필수] 국외이전 및 제3자 제공 동의
                    <Link
                      role="button"
                      className="ml-4!"
                      onClick={(e) => {
                        e.preventDefault();
                        openDialog2();
                      }}
                    >
                      전문보기
                    </Link>
                  </div>
                }
                control={
                  <Checkbox
                    checked={checked[3]}
                    slotProps={{ input: { ref: setRef(3) } }}
                    onChange={(e) => onChange(3, e.target.checked)}
                  />
                }
              />

              {error && (
                <FormHelperText error role="alert">
                  {error.message}
                </FormHelperText>
              )}
            </FormControl>

            <PrivacyAgreementDialog open={open1} onClose={close1} />
            <CrossBoardAgreementDialog open={open2} onClose={close2} />
          </>
        );
      }}
    />
  );
};
